import {
  GprRule,
  GprTerm,
  PutSiteHealthConfigRequest,
  evaluateGprRule,
  ruleRequiresDesignedPr,
  useSiteHealthConfig,
} from "@inrange/building-manager-api-client";
import { Site } from "@inrange/building-manager-api-client/models-site";
import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";

type TermType = "flat" | "fractionOfDesignedPr" | "designedPrMinus";
type RuleType = "single" | "max" | "min";

const TERM_LABELS: Record<TermType, string> = {
  flat: "Flat %",
  fractionOfDesignedPr: "% of Designed PR",
  designedPrMinus: "Designed PR minus % points",
};

const DEFAULT_TERM: Record<TermType, GprTerm> = {
  flat: { type: "flat", valuePct: 80 },
  fractionOfDesignedPr: { type: "fractionOfDesignedPr", fraction: 0.9 },
  designedPrMinus: { type: "designedPrMinus", deltaPct: 5 },
};

// Mirrors what the GprRuleEditor renders when its `rule` prop is null —
// keeping these in sync ensures the form's displayed default is what
// actually gets saved on submit (otherwise null would be persisted).
const DEFAULT_GPR_RULE: GprRule = DEFAULT_TERM.flat;

const termValueForInput = (term: GprTerm): number => {
  switch (term.type) {
    case "flat":
      return term.valuePct;
    case "fractionOfDesignedPr":
      return term.fraction * 100; // express as percentage
    case "designedPrMinus":
      return term.deltaPct;
  }
};

const setTermValueFromInput = (term: GprTerm, raw: number): GprTerm => {
  switch (term.type) {
    case "flat":
      return { type: "flat", valuePct: raw };
    case "fractionOfDesignedPr":
      return { type: "fractionOfDesignedPr", fraction: raw / 100 };
    case "designedPrMinus":
      return { type: "designedPrMinus", deltaPct: raw };
  }
};

const TermEditor = ({
  term,
  onChange,
}: {
  term: GprTerm;
  onChange: (next: GprTerm) => void;
}) => (
  <Row className="g-2 align-items-end">
    <Col md={7}>
      <Form.Select
        value={term.type}
        onChange={(e) => onChange(DEFAULT_TERM[e.target.value as TermType])}
      >
        {(Object.keys(TERM_LABELS) as TermType[]).map((t) => (
          <option key={t} value={t}>
            {TERM_LABELS[t]}
          </option>
        ))}
      </Form.Select>
    </Col>
    <Col md={5}>
      <Form.Control
        type="number"
        step="0.1"
        value={termValueForInput(term)}
        onChange={(e) => onChange(setTermValueFromInput(term, Number(e.target.value)))}
      />
    </Col>
  </Row>
);

const GprRuleEditor = ({
  rule,
  onChange,
}: {
  rule: GprRule | null;
  onChange: (next: GprRule | null) => void;
}) => {
  const ruleType: RuleType =
    rule == null
      ? "single"
      : rule.type === "max" || rule.type === "min"
        ? rule.type
        : "single";

  const terms: [GprTerm, GprTerm | null] = useMemo(() => {
    if (rule == null) return [DEFAULT_TERM.flat, null];
    if (rule.type === "max" || rule.type === "min") {
      return [rule.terms[0], rule.terms[1]];
    }
    return [rule, null];
  }, [rule]);

  const setRuleType = (next: RuleType) => {
    if (next === "single") {
      onChange(terms[0]);
    } else {
      const second = terms[1] ?? DEFAULT_TERM.fractionOfDesignedPr;
      onChange({ type: next, terms: [terms[0], second] });
    }
  };

  const setTerm = (idx: 0 | 1, nextTerm: GprTerm) => {
    if (ruleType === "single") {
      onChange(nextTerm);
    } else {
      const next: [GprTerm, GprTerm] = [
        idx === 0 ? nextTerm : terms[0],
        idx === 1 ? nextTerm : terms[1] ?? DEFAULT_TERM.fractionOfDesignedPr,
      ];
      onChange({ type: ruleType, terms: next });
    }
  };

  return (
    <div className="d-flex flex-column gap-2">
      <Form.Group>
        <Form.Label className="mb-1">Rule combinator</Form.Label>
        <Form.Select
          value={ruleType}
          onChange={(e) => setRuleType(e.target.value as RuleType)}
        >
          <option value="single">Single term</option>
          <option value="max">max() of two terms</option>
          <option value="min">min() of two terms</option>
        </Form.Select>
      </Form.Group>
      <TermEditor term={terms[0]} onChange={(t) => setTerm(0, t)} />
      {ruleType !== "single" && terms[1] != null && (
        <TermEditor term={terms[1]} onChange={(t) => setTerm(1, t)} />
      )}
    </div>
  );
};

const HealthMonitoringConfigModal = ({
  site,
  onClose,
}: {
  site: Site;
  onClose: () => void;
}) => {
  const { healthConfig, updateConfig, deleteConfig } = useSiteHealthConfig(
    site.id
  );
  const bmaConfig = healthConfig.data;

  const [alertingEnabled, setAlertingEnabled] = useState(
    bmaConfig?.alertingEnabled ?? true
  );
  const [lateDataThresholdHours, setLateDataThresholdHours] = useState(
    bmaConfig ? bmaConfig.lateDataThresholdSeconds / 3600 : 4
  );
  const [unreliableConnection, setUnreliableConnection] = useState(
    bmaConfig?.unreliableConnection ?? false
  );
  const [pvAvailability, setPvAvailability] = useState<string>(
    bmaConfig?.pvAvailability != null
      ? String(bmaConfig.pvAvailability)
      : bmaConfig?.contractedAvailability != null
        ? String(bmaConfig.contractedAvailability)
        : ""
  );
  const [bessAvailability, setBessAvailability] = useState<string>(
    bmaConfig?.bessAvailability != null
      ? String(bmaConfig.bessAvailability)
      : ""
  );
  const [designedPrPct, setDesignedPrPct] = useState<string>(
    bmaConfig?.designedPrPct != null ? String(bmaConfig.designedPrPct) : ""
  );
  const [gprRule, setGprRule] = useState<GprRule | null>(
    bmaConfig?.gprRule ??
      (bmaConfig?.contractedGpr != null
        ? { type: "flat", valuePct: bmaConfig.contractedGpr }
        : DEFAULT_GPR_RULE)
  );
  const [weatherActualsEnabled, setWeatherActualsEnabled] = useState(
    bmaConfig?.weatherActualsEnabled ?? false
  );
  const [maxImportAllowedKw, setMaxImportAllowedKw] = useState<string>(
    bmaConfig?.maxImportAllowedKw != null
      ? String(bmaConfig.maxImportAllowedKw)
      : ""
  );
  const [maxImportEnforcedKw, setMaxImportEnforcedKw] = useState<string>(
    bmaConfig?.maxImportEnforcedKw != null
      ? String(bmaConfig.maxImportEnforcedKw)
      : ""
  );
  const [inverterTempMonitoringEnabled, setInverterTempMonitoringEnabled] =
    useState(bmaConfig?.inverterTempMonitoringEnabled ?? false);
  const [inverterTempWarningC, setInverterTempWarningC] = useState<string>(
    bmaConfig?.inverterTempWarningC != null
      ? String(bmaConfig.inverterTempWarningC)
      : ""
  );
  const [inverterTempAlertC, setInverterTempAlertC] = useState<string>(
    bmaConfig?.inverterTempAlertC != null
      ? String(bmaConfig.inverterTempAlertC)
      : ""
  );
  const [error, setError] = useState<string | null>(null);

  // Import-limit fields are Reduxi-only (the check reads the Reduxi meter's
  // 15-min-avg import power). Gate on the same measurementType the BE check uses.
  const isReduxi = site.measurementType === "reduxi";
  // Inverter temperature comes only from SolarEdge inverter telemetry, so
  // the thresholds are meaningless on a Solis site (no `temperature`
  // readings) - hide them there.
  const isSolis = site.measurementType === "solis";
  const isSaving = updateConfig.isLoading;
  const isDeleting = deleteConfig.isLoading;

  const designedPrNumeric = designedPrPct ? Number(designedPrPct) : null;
  const evaluatedGpr = evaluateGprRule(gprRule, designedPrNumeric);
  const designedPrRequired = ruleRequiresDesignedPr(gprRule);
  const designedPrMissing = designedPrRequired && designedPrNumeric == null;

  const importAllowedNumeric = maxImportAllowedKw
    ? Number(maxImportAllowedKw)
    : null;
  const importEnforcedNumeric = maxImportEnforcedKw
    ? Number(maxImportEnforcedKw)
    : null;

  const inverterTempWarningNumeric = inverterTempWarningC
    ? Number(inverterTempWarningC)
    : null;
  const inverterTempAlertNumeric = inverterTempAlertC
    ? Number(inverterTempAlertC)
    : null;

  const handleSubmit = async () => {
    setError(null);
    if (designedPrMissing) {
      setError(
        "The selected GPR rule references Designed PR, so Designed PR must be set."
      );
      return;
    }
    if (
      importAllowedNumeric != null &&
      importEnforcedNumeric != null &&
      importEnforcedNumeric > importAllowedNumeric
    ) {
      setError(
        "Maximum import enforced by Reduxi must be ≤ maximum import allowed."
      );
      return;
    }
    // Only enforce ordering when monitoring is enabled, against the resolved
    // thresholds (blanks fall back to the 70/75 defaults the BE applies) — so a
    // dormant config isn't blocked over greyed-out inputs, and setting only the
    // warning to 80 can't silently persist an inverted 80/75 pair.
    if (inverterTempMonitoringEnabled) {
      const resolvedWarning = inverterTempWarningNumeric ?? 70;
      const resolvedAlert = inverterTempAlertNumeric ?? 75;
      if (resolvedAlert < resolvedWarning) {
        setError(
          "Inverter temperature alert threshold must be ≥ the warning threshold."
        );
        return;
      }
    }
    try {
      const payload: PutSiteHealthConfigRequest = {
        alertingEnabled,
        unreliableConnection,
        lateDataThresholdSeconds: Math.round(lateDataThresholdHours * 3600),
        pvAvailability: pvAvailability ? Number(pvAvailability) : null,
        bessAvailability: bessAvailability ? Number(bessAvailability) : null,
        // contractedAvailability retained for backwards compatibility: mirror the
        // PV target so old consumers keep working until the column drops.
        contractedAvailability: pvAvailability ? Number(pvAvailability) : null,
        designedPrPct: designedPrPct ? Number(designedPrPct) : null,
        gprRule,
        // contractedGpr retained for backwards compatibility: mirror the
        // evaluated GPR so old consumers keep working until the column drops.
        contractedGpr: evaluatedGpr,
        weatherActualsEnabled,
        maxImportAllowedKw: importAllowedNumeric,
        maxImportEnforcedKw: importEnforcedNumeric,
        inverterTempMonitoringEnabled,
        inverterTempWarningC: inverterTempWarningNumeric,
        inverterTempAlertC: inverterTempAlertNumeric,
      };
      await updateConfig.mutateAsync(payload);
      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to save health config";
      setError(errorMessage);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this health config?")) {
      return;
    }
    setError(null);
    try {
      await deleteConfig.mutateAsync();
      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to delete health config";
      setError(errorMessage);
    }
  };

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {bmaConfig ? "Edit" : "Configure"} health config
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {healthConfig.isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <Form>
            <h6 className="fw-bold mb-3">Alerting</h6>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Late data threshold (hours)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.5"
                    value={lateDataThresholdHours}
                    onChange={(e) =>
                      setLateDataThresholdHours(Number(e.target.value))
                    }
                  />
                </Form.Group>
              </Col>
              <Col
                md={4}
                className="d-flex flex-column justify-content-end gap-2"
              >
                <Form.Check
                  type="checkbox"
                  label="Alerting enabled"
                  checked={alertingEnabled}
                  onChange={(e) => setAlertingEnabled(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  label="Unreliable connection"
                  checked={unreliableConnection}
                  onChange={(e) => setUnreliableConnection(e.target.checked)}
                />
              </Col>
            </Row>

            <h6 className="fw-bold mb-3 mt-4">Contracted thresholds</h6>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>PV availability target (%)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    value={pvAvailability}
                    onChange={(e) => setPvAvailability(e.target.value)}
                    placeholder="e.g. 98.0"
                  />
                  <Form.Text className="text-muted">
                    PV system availability threshold for RYG status
                  </Form.Text>
                </Form.Group>
                <Form.Group>
                  <Form.Label>BESS availability target (%)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    value={bessAvailability}
                    onChange={(e) => setBessAvailability(e.target.value)}
                    placeholder="e.g. 97.0"
                  />
                  <Form.Text className="text-muted">
                    Battery 24-hour availability target
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Designed PR (%)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    value={designedPrPct}
                    onChange={(e) => setDesignedPrPct(e.target.value)}
                    placeholder="e.g. 85.0"
                  />
                  <Form.Text className="text-muted">
                    As stated in the EPC contract.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>Guaranteed PR rule</Form.Label>
                <GprRuleEditor rule={gprRule} onChange={setGprRule} />
                <Form.Text
                  className={designedPrMissing ? "text-danger" : "text-muted"}
                >
                  Effective GPR:{" "}
                  <strong>
                    {evaluatedGpr != null
                      ? `${evaluatedGpr.toFixed(1)} %`
                      : "—"}
                  </strong>
                  {designedPrMissing
                    ? " — this rule references Designed PR; set Designed PR above."
                    : " — Green if measured PR ≥ GPR, amber within 5 pp below, red beyond that. (Grades both the excluded and deemed GPR against this same bar.)"}
                </Form.Text>
              </Col>
            </Row>

            <h6 className="fw-bold mb-3 mt-4">Weather actuals</h6>
            <Row className="mb-3">
              <Col md={8}>
                <Form.Check
                  type="checkbox"
                  label="Enable daily weather actuals ingestion"
                  checked={weatherActualsEnabled}
                  onChange={(e) => setWeatherActualsEnabled(e.target.checked)}
                />
                <Form.Text className="text-muted">
                  Automatically ingest Meteonorm weather data daily for
                  performance ratio calculation
                </Form.Text>
              </Col>
            </Row>

            {isReduxi && (
              <>
                <h6 className="fw-bold mb-3 mt-4">Import limits (Reduxi)</h6>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Maximum import allowed (kW)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        value={maxImportAllowedKw}
                        onChange={(e) => setMaxImportAllowedKw(e.target.value)}
                        placeholder="e.g. 75"
                      />
                      <Form.Text className="text-muted">
                        Hard grid-connection limit. Alerts red if import exceeds
                        it.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Maximum import enforced by Reduxi (kW)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        value={maxImportEnforcedKw}
                        onChange={(e) => setMaxImportEnforcedKw(e.target.value)}
                        placeholder="e.g. 70"
                      />
                      <Form.Text className="text-muted">
                        Cap Reduxi is configured to hold (≤ allowed). Alerts amber
                        if import exceeds it.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}

            {!isSolis && (
              <>
              <h6 className="fw-bold mb-3 mt-4">Inverter temperature thresholds</h6>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Check
                    type="checkbox"
                    label="Enable temperature monitoring"
                    checked={inverterTempMonitoringEnabled}
                    onChange={(e) =>
                      setInverterTempMonitoringEnabled(e.target.checked)
                    }
                  />
                  <Form.Text className="text-muted">
                    Hourly per-inverter check (SolarEdge sites) that Slack-alerts
                    when an inverter runs hot.
                  </Form.Text>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Warning threshold (°C)</Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      value={inverterTempWarningC}
                      onChange={(e) => setInverterTempWarningC(e.target.value)}
                      placeholder="Default 70"
                      disabled={!inverterTempMonitoringEnabled}
                    />
                    <Form.Text className="text-muted">
                      Defaults to 70 °C warning if left blank.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Alert threshold (°C)</Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      value={inverterTempAlertC}
                      onChange={(e) => setInverterTempAlertC(e.target.value)}
                      placeholder="Default 75"
                      disabled={!inverterTempMonitoringEnabled}
                    />
                    <Form.Text className="text-muted">
                      Defaults to 75 °C alert if left blank.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              </>
            )}

            {error && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        {bmaConfig && (
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={isSaving || isDeleting}
            className="me-auto"
          >
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              "Delete config"
            )}
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isSaving || isDeleting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSaving || isDeleting || healthConfig.isLoading}
        >
          {isSaving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HealthMonitoringConfigModal;
